import { BrandingSchema, type Branding } from "../../schemata/biometry/Branding.js"
import type { UUIDv4 } from "../../schemata/shared/UUIDv4.js"
import HTTPClient from "../http/HTTPClient.js"
import HTTPHeader from "../http/HTTPHeader.js"
import HTTPMethod from "../http/HTTPMethod.js"
import HTTPRequestBuilder from "../http/HTTPRequestBuilder.js"
import MIMEType from "../http/MIMEType.js"
import Validate from "../language/Validate.js"

type Parameters = Readonly<{
	httpClient: HTTPClient
	integrationId: UUIDv4
	origin: string
	timeoutInMilliseconds: number
}>

/**
 * @description
 *	Retrieves the branding of an integration from the Biometry backend, or
 *	undefined when the integration has none.
 *
 * @remarks
 *	An integration without branding is not an error: every failure (404 of a
 *	missing item, 403 of a backend that predates the endpoint, 422, 5xx,
 *	timeout, network) and every unexpected payload resolve to undefined, so
 *	the landing stays neutral instead of breaking. Nothing is logged.
 *
 *	The origin is joined with the URL constructor instead of a template
 *	literal, because the configured origin is validated but not normalized: a
 *	trailing slash would produce a double slash, which the backend answers
 *	with a 403 of an unknown route.
 *
 *	That join stays outside of the guard on purpose. An origin that the URL
 *	constructor cannot use is a configuration mistake, not an integration
 *	without branding, and swallowing it here would hide it behind a landing
 *	that looks merely neutral: it is left to reject, and the caller of this
 *	function is the one that decides what a rejection looks like.
 */
const FetchBrandingAsync = async ({
	httpClient,
	integrationId,
	origin,
	timeoutInMilliseconds
}: Parameters): Promise<Branding | undefined> => {
	const request = new HTTPRequestBuilder<undefined>()
		.header(HTTPHeader.Accept, MIMEType.JSON)
		.method(HTTPMethod.GET)
		.timeout(timeoutInMilliseconds)
		.uri(new URL(`/branding/${integrationId}`, origin))
		.build()
	try {
		const response = await httpClient.sendAsync<undefined, unknown>(request)
		return Validate(BrandingSchema, response.body)
	} catch {
		return undefined
	}
}

export default FetchBrandingAsync
